import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/modern/components/ui/dialog-shadcn";
import { cn } from "@/modern/utils";
import { ReactNode } from "react";
import { EntityRecord } from "@/types/entity";

type MenuItemModalHeaderVariant = "title" | "header";
type SharedModalItemProps =
  | {
      variant: "title";
      modalTitle: string;
      modalDescription?: string;
    }
  | {
      variant: "header";
      ModalHeader: () => JSX.Element;
    };

type ModalItemProps = {
  children: ReactNode;
  record?: EntityRecord;
  classNameContent?: string;
} & SharedModalItemProps;

export const SharedModal = ({
  children,
  className,
  overlayClassName,
  onInteractOutside,
  onOpenAutoFocus,
  onCloseAutoFocus,
  showCloseButton = true,
}: {
  children: ReactNode;
  className?: string;
  overlayClassName?: string;
  onInteractOutside?: (e: Event) => void;
  onOpenAutoFocus?: (e: Event) => void;
  onCloseAutoFocus?: (e: Event) => void;
  showCloseButton?: boolean;
}) => {
  return (
    <SharedModalContent
      className={cn(" flex flex-col min-h-0 flex-1  gap-0", className)}
      overlayClassName={overlayClassName}
      onInteractOutside={onInteractOutside}
      onOpenAutoFocus={onOpenAutoFocus}
      onCloseAutoFocus={onCloseAutoFocus}
      showCloseButton={showCloseButton}
    >
      {children}
    </SharedModalContent>
  );
};

export const SharedModalTitle = ({ children, className, style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) => {
  return (
    <DialogTitle className={cn("text-lg max-w-[28rem]", className)} style={style}>
      {children}
    </DialogTitle>
  );
};

export const SharedModalHeader = ({ children, className, style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) => {
  return (
    <DialogHeader className={cn("border-b px-6  py-5 flex-none", className)} style={style}>
      {children}
    </DialogHeader>
  );
};

export const SharedModalDescription = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <DialogDescription className={cn("text-xs", className)}>{children}</DialogDescription>;
};

export const SharedModalBody = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={cn("px-6 flex-1 overflow-y-auto min-h-0 py-4", className)}>{children}</div>;
};

export const ModalItem = (props: ModalItemProps) => {
  return (
    <div className={cn("flex flex-col flex-1 min-h-0", props.classNameContent)}>
      <SharedModalHeader>
        {props.variant === "header" && <props.ModalHeader />}
        {props.variant === "title" && <SharedModalTitle>{props.modalTitle}</SharedModalTitle>}
        {props.variant === "title" && props.modalDescription && <SharedModalDescription>{props.modalDescription}</SharedModalDescription>}
      </SharedModalHeader>
      <SharedModalBody className={props.classNameContent}>{props.children}</SharedModalBody>
    </div>
  );
};

export const SharedModalContent = ({
  children,
  className,
  overlayClassName,
  closeButtonClassName = "",
  onPointerDownOutside,
  onInteractOutside,
  onEscapeKeyDown,
  onOpenAutoFocus,
  onCloseAutoFocus,
  showCloseButton = true
}: {
  children: ReactNode;
  className?: string;
  overlayClassName?: string;
  closeButtonClassName?: string;
  onPointerDownOutside?: (e: Event) => void;
  onInteractOutside?: (e: Event) => void;
  onEscapeKeyDown?: (e: KeyboardEvent) => void;
  onOpenAutoFocus?: (e: Event) => void;
  onCloseAutoFocus?: (e: Event) => void;
  showCloseButton?: boolean;
}) => {
  return (
    <DialogContent
      showCloseButton={showCloseButton}
      closeButtonClassName={cn("[&_svg:not([class*='size-'])]:size-6 -top-5 -right-5 bg-background p-3 rounded-full border opacity-100", closeButtonClassName)}
      overlayClassName={cn("z-[--swivu-bryntum-z-index]", overlayClassName)}
      className={cn(
        "z-[--swivu-bryntum-z-index] p-0 max-h-[90dvh] flex md:max-w-screen-lg top-[5dvh] translate-y-0 data-[state=open]:slide-in-from-top-[10%] data-[state=closed]:slide-out-to-top-[10%] data-[state=open]:slide-in-from-left-1/2 data-[state=closed]:slide-out-to-left-1/2 ",
        className || undefined
      )}
      onPointerDownOutside={onPointerDownOutside}
      onInteractOutside={onInteractOutside}
      onEscapeKeyDown={onEscapeKeyDown}
      onOpenAutoFocus={onOpenAutoFocus}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      {children}
    </DialogContent>
  );
};

export type { MenuItemModalHeaderVariant, SharedModalItemProps, ModalItemProps };
